/**
 * Sprint 6: UI primitives — loading / error / empty states + form input.
 *
 * Centralized so every page uses the same affordance. Translated strings
 * are kept inline (uz-Cyrl) because i18n key plumbing for these is not
 * done in Sprint 6 — they are read by users directly.
 */
import React from "react";
import { Inbox } from "lucide-react";

/** Loading skeleton block. */
export function Loading({ label = "Юкланмоқда..." }: { label?: string }) {
  return (
    <div className="state-loading" role="status" aria-live="polite">
      <div className="spinner" />
      <span>{label}</span>
    </div>
  );
}

/** Inline error with retry hook. */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state-error" role="alert">
      <strong>Хатолик:</strong> <span>{message}</span>
      {onRetry && (
        <button className="btn-secondary" onClick={onRetry}>
          Қайта уриниш
        </button>
      )}
    </div>
  );
}

/**
 * Sprint 25 D6: Standardized empty state.
 * Every page should use this instead of custom inline empty UI.
 *
 * Props:
 *   message   — primary text (default: "Маълумот топилмади")
 *   action    — optional { label, onClick }
 */
export function EmptyState({
  message = "Маълумот топилмади",
  icon,
  action,
}: {
  message?: string;
  icon?: React.ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div
      className="state-empty"
      role="status"
    >
      <div className="state-empty-icon">
        {icon ?? <Inbox size={36} />}
      </div>
      <div className="state-empty-message">{message}</div>
      {action && (
        <button
          className="btn btn-primary btn-sm"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Legacy alias — prefer EmptyState */
export const Empty = EmptyState;

/** Form field wrapper with label + error display. */
export function FormField({
  label,
  error,
  children,
  required,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="form-field">
      <span className="form-label">
        {label}
        {required && <span className="required-mark"> *</span>}
      </span>
      {children}
      {error && <span className="form-error">{error}</span>}
    </label>
  );
}
